import React, { useState, useEffect } from 'react';
import { User, Check, X, Loader, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { updateFriendRequest } from '../lib/friends';
import { useLanguage } from '../contexts/LanguageContext';

const PendingFriendRequests: React.FC = () => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingRequests, setPendingRequests] = useState<any[]>([]);
  const [processingRequest, setProcessingRequest] = useState<string | null>(null);

  useEffect(() => {
    fetchPendingRequests();
  }, []);

  const fetchPendingRequests = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;
      if (!user) throw new Error('Not authenticated');

      // Fetch pending friend requests where the current user is the receiver
      const { data, error: requestsError } = await supabase
        .from('friends')
        .select(`
          id,
          status,
          created_at,
          sender:profiles!friends_sender_id_fkey(
            id,
            full_name,
            profile_picture,
            location
          )
        `)
        .eq('receiver_id', user.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (requestsError) throw requestsError;
      setPendingRequests(data || []);
    } catch (err) {
      console.error('Error fetching pending friend requests:', err);
      setError(err instanceof Error ? err.message : 'Failed to load friend requests');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestAction = async (requestId: string, action: 'accepted' | 'rejected') => {
    try {
      setProcessingRequest(requestId);
      setError(null);

      await updateFriendRequest(requestId, action);
      
      // Remove the request from the list
      setPendingRequests(prev => prev.filter(request => request.id !== requestId));
    } catch (err) {
      console.error(`Error ${action === 'accepted' ? 'accepting' : 'rejecting'} friend request:`, err);
      setError(err instanceof Error ? err.message : `Failed to ${action === 'accepted' ? 'accept' : 'reject'} friend request`);
    } finally {
      setProcessingRequest(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center">
        <AlertCircle className="h-5 w-5 mr-2" />
        <span>{error}</span>
      </div>
    );
  }

  if (pendingRequests.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <User className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <p>{t('profile.noPendingRequests')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="font-medium text-gray-700">{t('profile.pendingRequests')}</h3>
      
      {pendingRequests.map((request) => (
        <div 
          key={request.id} 
          className="flex items-center justify-between bg-white p-4 rounded-lg shadow-sm hover:shadow transition"
        >
          <div className="flex items-center space-x-3">
            {request.sender.profile_picture ? (
              <img
                src={request.sender.profile_picture}
                alt={request.sender.full_name}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                <User className="h-5 w-5 text-gray-400" />
              </div>
            )}
            <div>
              <h3 className="font-medium text-gray-900">{request.sender.full_name}</h3>
              {request.sender.location && (
                <p className="text-sm text-gray-500">{request.sender.location}</p>
              )}
            </div>
          </div>
          
          <div className="flex space-x-2">
            <button
              onClick={() => handleRequestAction(request.id, 'rejected')}
              disabled={processingRequest === request.id}
              className={`p-2 bg-red-100 text-red-600 rounded-full hover:bg-red-200 transition ${
                processingRequest === request.id ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              title={t('profile.rejectRequest')}
            >
              {processingRequest === request.id ? (
                <Loader className="h-5 w-5 animate-spin" />
              ) : (
                <X className="h-5 w-5" />
              )}
            </button>
            <button
              onClick={() => handleRequestAction(request.id, 'accepted')}
              disabled={processingRequest === request.id}
              className={`p-2 bg-green-100 text-green-600 rounded-full hover:bg-green-200 transition ${
                processingRequest === request.id ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              title={t('profile.acceptRequest')}
            >
              {processingRequest === request.id ? (
                <Loader className="h-5 w-5 animate-spin" />
              ) : (
                <Check className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default PendingFriendRequests;